import _ from 'lodash';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { BrmActionType } from 'src/common/enumerations/brm-action-type';
import { InterventionsMode } from 'src/common/interfaces/states/IInterventionsState';
import { updateCartStatus } from 'src/home-page/business-logic/update-cart-status';
import {
  voidEmptyTransaction,
  voidTransactionOnPromotionDown,
} from 'src/home-page/business-logic/void-transaction';
import {
  ActionRequiredType,
  AgeConfirmationRejectReason,
  RestrictionType,
} from 'src/items-list/business-logic/service/enumerations/cart-restrictions-types';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { InterventionsNotFound } from '../../common/helpers/error-code';
import { getValueAsNumber } from '../../common/helpers/number-helper';
import { IItemLookup } from '../../common/interfaces/dtos/IItemLookup';
import { IElectronicJournalRecord } from '../../common/interfaces/states/IElectronicJournalRecord';
import { store } from '../../common/redux/configure-store';
import { InterventionsActions } from '../../common/redux/reducers/interventions-reducer';
import { lookupItemById } from '../../items-list/business-logic/item-lookup';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { IIntervention, IInterventionGroup, IInterventionInfo } from '../interfaces/IIntervention';
import { handleInterventions } from './handle-interventions';
import { IInterventionLookupResult, interventionLookup } from './service/intervention-service';
import {
  voidInterventionItemsCategorized,
  VoidInterventionReason,
  VoidItem,
} from './void-intervention-rejected-items';

export const displayInterventions = (): void => {
  getInterventions(true, true).catch(popupMessage);
};

const getVoidItems = (interventions: IInterventionInfo[]): VoidItem[] =>
  interventions.map(intervention => ({
    itemId: intervention.itemId,
    sequenceNumber: intervention.itemSequenceNumber,
    description: intervention.itemLookup?.description,
  }));

const lookupAndHandleVoidInterventions = async (): Promise<
  IInterventionLookupResult | undefined | void
> => {
  const { recallUpdateCartStatus, voidRejectInterventionsPreSolve } = getCurrentXLRConfig();
  const interventionData = await lookupInterventions().catch(async err => {
    if (err.code === InterventionsNotFound) {
      store.dispatch(InterventionsActions.resetState());
      const itemList = store.getState().itemList;

      if (recallUpdateCartStatus) {
        await updateCartStatus();
      }
      if (!itemList.itemCount) {
        voidEmptyTransaction();
      }
      voidTransactionOnPromotionDown();
    } else {
      throw err;
    }
  });

  if (interventionData) {
    setInterventionMode(interventionData);
    const interventionGroups = buildInterventionGroups(interventionData);
    if (interventionGroups.length) {
      store.dispatch(InterventionsActions.setInterventions(interventionGroups));
    }

    if (voidRejectInterventionsPreSolve) {
      const dict = new Map<VoidInterventionReason, VoidItem[]>();

      if (interventionData.interventionInfo) {
        const interventions: IInterventionInfo[] = interventionData?.interventionInfo;
        // group by restrictionType here because buildInterventionGroups might change later on
        const groupedInterventions = _.groupBy(
          interventions,
          (x): string => x.restriction?.restrictionType,
        );

        Object.keys(groupedInterventions).forEach((groupKey): void => {
          const interventionsInGroup = groupedInterventions[groupKey];

          switch (groupKey) {
            case RestrictionType.AgeRestriction: {
              const customerUnderageInterventions: IInterventionInfo[] = [];
              const prohibitRestrictedItems: IInterventionInfo[] = [];

              interventionsInGroup.forEach(intervention => {
                const rejectedReason =
                  intervention.restriction.additionalData?.ageConfirmation?.rejectedReason;
                if (intervention.restriction.requiredAction === ActionRequiredType.Prohibit) {
                  prohibitRestrictedItems.push(intervention);
                } else if (
                  rejectedReason &&
                  rejectedReason !== AgeConfirmationRejectReason.MissingInput
                ) {
                  customerUnderageInterventions.push(intervention);
                }
              });

              if (customerUnderageInterventions.length > 0) {
                dict.set(
                  VoidInterventionReason.VoidInterventionCustomerUnderAge,
                  getVoidItems(customerUnderageInterventions),
                );
              }

              if (prohibitRestrictedItems.length > 0) {
                dict.set(
                  VoidInterventionReason.VoidInterventionProhibit,
                  getVoidItems(prohibitRestrictedItems),
                );
              }
              break;
            }
            case RestrictionType.LockForSale: {
              dict.set(
                VoidInterventionReason.VoidInterventionLockForSale,
                getVoidItems(interventionsInGroup),
              );
              break;
            }
          }
        });
      }

      if (dict.size > 0) {
        await voidInterventionItemsCategorized(dict);
        return lookupAndHandleVoidInterventions();
      }
    }
  }

  return interventionData;
};

export const getInterventions = async (showModal = true, showEmptyModal = false): Promise<void> => {
  store.dispatch(InterventionsActions.resetState());
  await lookupAndHandleVoidInterventions();
  if (store.getState().interventions.groups.length > 0) {
    if (showModal) {
      return handleInterventions();
    }
  }
  if (showEmptyModal) {
    return handleInterventions();
  }
};

const buildInterventions = (interventionData: IInterventionLookupResult): IIntervention[] => {
  let interventions: IIntervention[] = [];
  if (interventionData.hardwareInterventions) {
    interventionData.hardwareInterventions.forEach((hardwareIntervention): void => {
      interventions.push({
        ...hardwareIntervention,
        isHardwareIntervention: true,
      });
    });
  }
  if (interventionData.interventionInfo) {
    interventions = interventions.concat(interventionData.interventionInfo);
  }
  return interventions;
};

const buildInterventionGroups = (
  interventionData: IInterventionLookupResult,
): IInterventionGroup[] => {
  const interventions: IIntervention[] = buildInterventions(interventionData);

  if (!interventions.length) {
    return [];
  }
  const interventionsByGroup = _.groupBy(interventions, (x): string => x.operationName);
  const interventionGroups: IInterventionGroup[] = [];
  Object.keys(interventionsByGroup).forEach((groupKey): void => {
    const key = groupKey === 'ItemStatusLockedForSaleException' ? 'ItemLockedForSale' : groupKey;
    //TODO: In future add call Translationmessageutil
    const interventionsInGroup = interventionsByGroup[groupKey];
    const interventionsInGroupLength = interventionsInGroup.length;
    //TODO: In future implement hardwareinterventions
    // UpdateSCOInterventionGroupsDataModel(interventionsInGroup);
    interventionGroups.push({
      groupName: key,
      interventions: interventionsInGroup,
      interventionCount: interventionsInGroupLength,
    });
  });
  interventionGroups.reverse();
  return interventionGroups;
};

const lookupInterventions = async (): Promise<IInterventionLookupResult | undefined> => {
  let interventionData: IInterventionLookupResult | undefined;
  await withSpinner(async () => {
    interventionData = await interventionLookup();
    await updateResponseWithItemLookup(interventionData);
    return interventionData;
  });

  return interventionData;
};

const updateResponseWithItemLookup = async (
  interventionData: IInterventionLookupResult,
): Promise<IInterventionLookupResult> => {
  const excludedInterventionTypes: string[] = [
    'GeneralIntervention',
    'EftFailed',
    'CustomerNotFound',
  ];
  const items: IElectronicJournalRecord[] = store.getState().itemList.ejItems;
  if (interventionData?.interventionInfo) {
    for (const intervention of interventionData.interventionInfo) {
      const shouldUpdate: boolean =
        !intervention.itemId || excludedInterventionTypes.includes(intervention.type);
      if (!shouldUpdate) {
        const interventionItemSequenceNumber: number = intervention.itemSequenceNumber;
        const itemToLookup = items.find(
          (ejItem: IElectronicJournalRecord): boolean =>
            ejItem.SequenceNumber === interventionItemSequenceNumber,
        );
        let itemLookupResult: IItemLookup | undefined;
        if (itemToLookup) {
          itemLookupResult = {
            itemId: itemToLookup.ItemId,
            description: itemToLookup.Name,
            unitPrice: getValueAsNumber(itemToLookup.UnitPrice),
          };
          intervention.amount = getValueAsNumber(itemToLookup.RawPrice);
        } else {
          const itemId = intervention.itemId;
          await lookupItemById({ data: itemId })
            .then(lookupResult => {
              if (lookupResult?.itemId === itemId) {
                itemLookupResult = lookupResult;
              }
            })
            .catch(() => null);
        }
        intervention.itemLookup = itemLookupResult;
      }
      await lookupPriceEntryItem(intervention);
    }
  }
  return interventionData;
};

const setInterventionMode = (interventionData: IInterventionLookupResult): void => {
  const isRecallOrder = interventionData.interventionInfo?.find(i =>
    i.type.includes('RecallOrder'),
  );
  store.dispatch(
    InterventionsActions.setInterventionsMode(
      isRecallOrder ? InterventionsMode.RecallOrder : InterventionsMode.Normal,
    ),
  );
};

const lookupPriceEntryItem = async (interventionInfo: IInterventionInfo) => {
  if (
    interventionInfo.itemLookup?.description === undefined &&
    interventionInfo.restriction?.additionalData.restrictionCode ===
      BrmActionType.PriceEntryRequired
  ) {
    const itemId =
      interventionInfo.restriction.additionalData.interventionNeededOn.itemRequest.scanData;
    let itemLookupResult;
    await lookupItemById({ data: itemId })
      .then(lookupResult => {
        itemLookupResult = lookupResult;
      })
      .catch(() => null);
    interventionInfo.itemLookup = itemLookupResult;
    interventionInfo.itemId = itemLookupResult?.itemId;
  }
};
